import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  AlertBanner,
  Box,
  Button,
  Checkbox,
  Icon,
  InlineMessage,
  Loader,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { setConsultActionAssigneeLoading } from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_APPLICATION_OWNER,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  CAMPAIGNTYPE_SERVICEACCOUNT,
  CAMPAIGNTYPE_USERMANAGER,
} from '../../../utils/constant';
import { getMessageKey } from '../../../utils/utils';
import AriaErrors from '../../AriaErrors';
import { enableReassignPopover } from '../../CertsActionButtons/CertsActionButton/utils';
import CertsModalCommentBox, {
  useCertsModalCommentErrors,
} from '../../CertsModalCommentBox';
import {
  BULK_ACTION_FOOTER_OBJECTS,
  BULK_ACTION_FOOTER_OBJECTS_MESSGAE_KEYS,
} from '../constant';
import { BULK_ACTION_REASSIGN } from '../mockData';

import BulkActionReassignList from './BulkActionReassignList';
import {
  BULK_ACTION_FOOTER_BTNS_REVERSE,
  BULK_ACTION_OBJ_PARENT_ENTITLEMENTS,
  BULK_ACTION_OBJ_USERS,
  BULK_ACTION_REASSIGN_ICON_MAP_FOR_STATS,
  COMMENT_MINIMUM_LENGTH,
  getBulkActionFooterBtns,
} from './constant';

function BulkActionReassignBody({
  bulkFooterStats,
  bulkActionReassign,
  setBulkActionReassign,
  handleCloseBulkActionReassign,
  handleCloseBukActionReassignAlert,
  bulkPredefinedComments,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const {
    global: { certKey, backNavigation },
    lineActions: { consultActionAssignee, reassignConfigMap },
    reviewAndSubmit: { primaryCertifier: isPrimaryCertifier },
    core: { campaignType },
  } = useSelector((state) => state?.certifications?.details);

  const { messages } = useSelector((state) => state?.user);

  const { predefinedComments: { allowEditPredefinedComments = false } = {} } =
    useSelector((state) => state?.certifications?.details?.core || {});

  const { isSecondaryCertifierLoggedIn } = reassignConfigMap;
  const isReassignActionAssigneeLoading = Boolean(
    consultActionAssignee?.isLoading
  );
  const isReassignActionAssigneeError = Boolean(
    consultActionAssignee?.errorMessage?.error !== undefined &&
      consultActionAssignee?.errorMessage?.error !== null &&
      consultActionAssignee?.errorMessage?.error?.trim()?.length !== 0
  );
  const buttonKey = BULK_ACTION_FOOTER_BTNS_REVERSE?.[bulkActionReassign?.id];
  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const reassignObjectKeys = Object?.keys(bulkFooterStats).filter((value) =>
    getBulkActionFooterBtns(campaignType, reassignConfigMap)[
      buttonKey
    ]?.objectsToTotal.includes(value)
  );

  const shouldConvertToReassign = Boolean(
    isPrimaryCertifier &&
      CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_USERMANAGER &&
      (reassignConfigMap?.enableReassignUser === '1' ||
        reassignConfigMap?.showReassignPopup === '1')
  );

  // AO and SA for Secondary certifier logged in
  const isCertifierNeededForAOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_APPLICATION_OWNER &&
    (isSecondaryCertifierLoggedIn || !backNavigation?.consulteeLoggedIn);

  // // SA certifier required
  const isCertifierNeededForSACampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT &&
    enableReassignPopover(reassignConfigMap) &&
    (isSecondaryCertifierLoggedIn || !backNavigation.consulteeLoggedIn);

  const isCertifierNeededforEOCampaign =
    isEOCampaign && enableReassignPopover(reassignConfigMap);

  useEffect(() => {
    if (bulkActionReassign?.isOpen) {
      dispatch(setConsultActionAssigneeLoading(false));
    }
  }, [bulkActionReassign?.isOpen]);

  const handleCommentDescription = (text) => {
    setBulkActionReassign((prev) => ({
      ...prev,
      comment: text,
    }));
  };

  const handleHasAgreedChange = () => {
    setBulkActionReassign((prev) => ({
      ...prev,
      hasAgreed: !prev.hasAgreed,
    }));
  };

  const renderReassignActionBody = () => {
    if (isReassignActionAssigneeLoading) {
      return (
        <Box className='BulkActionReassignBody-loaderWrapper'>
          <Loader
            kind='dots'
            format='inline'
            color='brand'
            size='large'
            className='BulkActionReassignBody-loader'
          />
        </Box>
      );
    }

    if (isReassignActionAssigneeError) {
      return (
        <Box className='AccessRequestHistoryBody-wrapper'>
          <div className='ReviewAndSubmitModal-bodyError'>
            <Typography kind='h2'>
              {messages['COULD_NOT_LOAD.ATTESTATIONS'] ||
                localize(intl, getMessageKey('COULD_NOT_LOAD'))}
            </Typography>
            <Typography kind='body2'>
              {messages['Failed.MODAL.ATTESTATIONS'] ||
                localize(intl, getMessageKey('Failed.MODAL'))}
            </Typography>
            <div className='ReviewAndSubmitModal-bodyErrorButtons'>
              <Button
                type='button'
                kind='ghost'
                size='medium'
                onClick={handleCloseBulkActionReassign}>
                {messages['CloseLabel.ATTESTATIONS'] ||
                  localize(intl, getMessageKey('CloseLabel'))}
              </Button>
            </div>
          </div>
        </Box>
      );
    }

    return (
      <>
        <Box tag='div' className='BulkActionReassignBody-stats'>
          <Typography
            kind='label'
            className='BulkActionReassignBody-statsHeading'>
            {(() => {
              const selectedUsersLabel =
                messages.CERT_BULK_REASSIGN_SELECTED_USERS ||
                localize(
                  intl,
                  getMessageKey('CERT_BULK_REASSIGN_SELECTED_USERS')
                );

              const selectedItemsLabel =
                messages.CERT_BULK_REASSIGN_SELECTED_ITEMS ||
                localize(
                  intl,
                  getMessageKey('CERT_BULK_REASSIGN_SELECTED_ITEMS')
                );

              return bulkActionReassign?.id ===
                getBulkActionFooterBtns(campaignType, reassignConfigMap)
                  ?.DOES_NOT_WORK_FOR_ME.id && !isEOCampaign
                ? selectedUsersLabel
                : selectedItemsLabel;
            })()}
            {(() => {
              let messageKey = '';
              const isMultiple = isEOCampaign
                ? bulkFooterStats?.[BULK_ACTION_OBJ_PARENT_ENTITLEMENTS] > 1
                : bulkFooterStats?.[BULK_ACTION_OBJ_USERS] > 1;

              if (isMultiple) {
                messageKey = isEOCampaign
                  ? 'CERT_BULK_CONSULT_TOOLTIP_MULTIPLE_EO'
                  : 'CERT_BULK_CONSULT_TOOLTIP_MULTIPLE';
              } else {
                messageKey = isEOCampaign
                  ? 'CERT_BULK_CONSULT_TOOLTIP_SINGLE_EO'
                  : 'CERT_BULK_CONSULT_TOOLTIP_SINGLE';
              }

              const accessMessageLabel =
                messages.messageKey ||
                localize(intl, getMessageKey(messageKey));

              const dependentMessageLabel =
                messages.CERT_BULK_REASSIGN_DEPENDENT_MSG ||
                localize(
                  intl,
                  getMessageKey('CERT_BULK_REASSIGN_DEPENDENT_MSG')
                );

              return (
                <Tooltip
                  openDelay={500}
                  text={
                    bulkActionReassign?.id ===
                    getBulkActionFooterBtns(campaignType, reassignConfigMap)
                      ?.DOES_NOT_WORK_FOR_ME.id
                      ? accessMessageLabel
                      : dependentMessageLabel
                  }
                  maxWidth='20rem'
                  placement='right'
                  offset={8}
                  trigger={
                    <Icon
                      kind='AlertInfoFilledSeparateColors'
                      color='--color-foreground-info-medium'
                      size='small'
                      className='BulkActionReassignBody-statsHeadingIcon'
                    />
                  }
                />
              );
            })()}
          </Typography>
          <div className='BulkActionReassignBody-statsObjects'>
            {reassignObjectKeys?.map((key) => {
              const messageKey =
                BULK_ACTION_FOOTER_OBJECTS_MESSGAE_KEYS?.[
                  `${BULK_ACTION_FOOTER_OBJECTS?.[key]}${
                    bulkFooterStats?.[key] > 1 ? 's' : ''
                  }`
                ];

              return (
                <Typography
                  key={key}
                  kind='body2'
                  className='BulkActionReassignBody-statsContent'>
                  <Icon
                    kind={BULK_ACTION_REASSIGN_ICON_MAP_FOR_STATS?.[key].icon}
                    size='medium'
                  />
                  {`${bulkFooterStats?.[key]} ${
                    messages.messageKey ||
                    localize(intl, getMessageKey(messageKey))
                  }`}
                </Typography>
              );
            })}
          </div>
        </Box>
        {(shouldConvertToReassign ||
          isCertifierNeededForAOCampaign ||
          isCertifierNeededForSACampaign ||
          isCertifierNeededforEOCampaign) && (
          <Box tag='div' className='BulkActionReassignBody-assigneeBox'>
            <Typography
              kind='label'
              className='BulkActionReassignBody-statsHeading'>
              {bulkActionReassign?.reassignTo.length === 0 && (
                <Box
                  className={
                    bulkActionReassign?.showAlert &&
                    bulkActionReassign?.reassignTo.length === 0
                      ? 'BulkActionReassignBody-asterisk--isCritical BulkActionReassignBody-asterisk'
                      : 'BulkActionReassignBody-asterisk'
                  }>
                  *
                </Box>
              )}
              {messages.CERT_BULK_REASSIGN_SELECT_CERTIFIER ||
                localize(
                  intl,
                  getMessageKey('CERT_BULK_REASSIGN_SELECT_CERTIFIER')
                )}
            </Typography>
            <BulkActionReassignList
              bulkActionReassign={bulkActionReassign}
              setBulkActionReassign={setBulkActionReassign}
              isCritical={
                bulkActionReassign?.reassignTo === '' &&
                bulkActionReassign?.showAlert
              }
              certKey={certKey}
              showDefaultCertifier={isPrimaryCertifier}
              defaultReassignee={reassignConfigMap?.defaultReassignee}
              defaultAssigneName={reassignConfigMap?.defaultReassigneeName}
              placeholder={
                messages['BULKACTION.choose.assignee.ATTESTATIONS'] ||
                localize(intl, getMessageKey('BULKACTION.choose.assignee'))
              }
              certifierUserKey={reassignConfigMap?.certifierUserKey}
            />
          </Box>
        )}
        <CertsModalCommentBox
          defaultComments={bulkPredefinedComments}
          isDisabled={bulkActionReassign?.isSubmitting}
          showAlert={
            bulkActionReassign?.showAlert &&
            bulkActionReassign?.comment?.replace(/^\s+|\s+$/gm, '')?.length <=
              COMMENT_MINIMUM_LENGTH
          }
          onChange={handleCommentDescription}
          clearModal={bulkActionReassign?.isOpen}
          label={messages.comments || localize(intl, getMessageKey('comments'))}
          allowEditPredefinedComments={allowEditPredefinedComments}
        />
        <div className='BulkActionsBody-checkBoxDiv'>
          <Checkbox
            id='bulk-action-reassign-checkbox-3'
            className='BulkActionBody-reassignCheckbox'
            name='checkbox-error'
            kind='singleLine'
            dataTestId='checkbox-error'
            isDisabled={bulkActionReassign?.isSubmitting}
            isChecked={bulkActionReassign?.hasAgreed}
            isRequired={!bulkActionReassign?.hasAgreed}
            isCritical={
              bulkActionReassign?.showAlert && !bulkActionReassign?.hasAgreed
            }
            onChange={handleHasAgreedChange}
            label={
              messages['BULKACTION.confirm.message.ATTESTATIONS'] ||
              localize(intl, getMessageKey('BULKACTION.confirm.message'))
            }
            textPosition='after'
            HelperText={
              bulkActionReassign?.showAlert &&
              !bulkActionReassign?.hasAgreed && (
                <InlineMessage
                  text={
                    messages['Required.field.ATTESTATIONS'] ||
                    localize(intl, getMessageKey('Required.field'))
                  }
                  colorTheme='critical'
                  size='small'
                  leftIcon={<Icon kind='AlertCriticalFilled' />}
                />
              )
            }
          />
        </div>
      </>
    );
  };

  const commentErrors = useCertsModalCommentErrors(bulkActionReassign?.comment);

  const isAlertVisible = Boolean(
    bulkActionReassign?.showAlert && !bulkActionReassign?.isSubmitting
  );

  const commonBodyClass = classnames(
    'BulkActionReassignBody',
    isAlertVisible && 'BulkActionReassignBody-alertBox--1'
  );

  return (
    <>
      <AlertBanner
        colorTheme='Critical'
        title={
          messages['Required.fields.missing.ATTESTATIONS'] ||
          localize(intl, getMessageKey('Required.fields.missing'))
        }
        description=''
        isVisible={
          bulkActionReassign?.showAlert && !bulkActionReassign?.isSubmitting
        }
        isColonVisible={false}
        onCancel={handleCloseBukActionReassignAlert}
        isFloating
        position={{
          type: 'absolute',
          top: '0',
        }}
      />
      <div className={commonBodyClass}>{renderReassignActionBody()}</div>
      <AriaErrors
        isCritical={bulkActionReassign?.showAlert}
        messages={[
          ...commentErrors,
          (shouldConvertToReassign ||
            isCertifierNeededForAOCampaign ||
            isCertifierNeededForSACampaign ||
            isCertifierNeededforEOCampaign) &&
            bulkActionReassign?.reassignTo?.length === 0 &&
            localize(intl, getMessageKey('certifier.required')),
          !bulkActionReassign?.hasAgreed &&
            (messages?.['confirmation.required.ATTESTATIONS'] ||
              localize(intl, getMessageKey('confirmation.required'))),
        ]}
      />
    </>
  );
}

BulkActionReassignBody.propTypes = {
  bulkFooterStats: PropTypes.shape({}),
  bulkActionReassign: PropTypes.shape(BULK_ACTION_REASSIGN).isRequired,
  setBulkActionReassign: PropTypes.func.isRequired,
  handleCloseBulkActionReassign: PropTypes.func.isRequired,
  handleCloseBukActionReassignAlert: PropTypes.func,
  bulkPredefinedComments: PropTypes.arrayOf(PropTypes.string),
};

BulkActionReassignBody.defaultProps = {
  bulkFooterStats: {},
  handleCloseBukActionReassignAlert: () => {},
  bulkPredefinedComments: [],
};

export default BulkActionReassignBody;
